import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { useGetToolSchema } from '@/modules/gpt-chats/hooks/use-tools';
import { showErrorToast } from '@/hooks/use-toast';
import { Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface EditSwaggerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (value: string) => void;
  toolId: string;
  isBlocksTool?: boolean;
}

export const EditSwaggerModal = ({
  isOpen,
  onClose,
  toolId,
  isBlocksTool,
}: EditSwaggerModalProps) => {
  const { t } = useTranslation();
  const {
    data: schemaData,
    isLoading: isSchemaLoading,
    error: schemaError,
  } = useGetToolSchema(toolId, isBlocksTool, isOpen);

  const [value, setValue] = useState('');

  useEffect(() => {
    if (schemaError) {
      showErrorToast({
        title: t('FAILED_TO_LOAD_SCHEMA'),
        errors: schemaError,
      });
    }
  }, [schemaError, t]);

  useEffect(() => {
    if (schemaData?.schema) {
      setValue(JSON.stringify(schemaData.schema, null, 2));
    } else if (isOpen) {
      setValue('');
    }
  }, [schemaData, isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t('EDIT_SWAGGER')}</DialogTitle>
        </DialogHeader>

        <div className="h-[500px] rounded border">
          {isSchemaLoading ? (
            <div className="flex h-full items-center justify-center">
              <div>
                <Loader className="mx-auto mb-2 h-6 w-6 animate-spin text-muted-foreground" />
                <div className="text-sm text-muted-foreground">{t('LOADING_SCHEMA')}</div>
              </div>
            </div>
          ) : (
            <textarea
              className="h-full w-full resize-none rounded bg-background p-4 font-mono text-sm outline-none"
              value={value}
              readOnly
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
